import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API_CONFIG } from '../config/api';

interface SavedImageState {
  img: HTMLImageElement;
  src: string;
  display: string;
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function waitForImage(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return;
  await new Promise<void>((resolve) => {
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
}

async function waitForCaptureImages(el: HTMLElement): Promise<void> {
  const images = el.querySelectorAll('img');
  await Promise.all(Array.from(images).map((img) => waitForImage(img)));
}

async function waitForElementById(id: string, maxMs = 5000): Promise<HTMLElement> {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    const el = document.getElementById(id);
    if (el) return el;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }
  throw new Error('Invoice preview element not found');
}

/**
 * Same image, routed through our backend so it comes back with CORS headers.
 * The S3 objects answer 200 but send no Access-Control-Allow-Origin, which is
 * why a direct fetch fails even though the <img> renders fine on screen.
 */
function proxied(src: string): string {
  return `${API_CONFIG.BASE_URL}/api/v1/common/imageProxy?url=${encodeURIComponent(src)}`;
}

async function fetchAsDataUrl(url: string): Promise<string> {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return blobToDataUrl(await response.blob());
}

async function inlineRemoteImages(el: HTMLElement): Promise<SavedImageState[]> {
  const saved: SavedImageState[] = [];
  const images = el.querySelectorAll('img');

  await Promise.all(
    Array.from(images).map(async (img) => {
      const src = img.getAttribute('src') || img.src;
      if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;
      if (!src.startsWith('http')) return;

      saved.push({ img, src, display: img.style.display });

      try {
        img.src = await fetchAsDataUrl(src);
        await waitForImage(img);
        return;
      } catch (directErr) {
        // Expected for our own S3 media — no ACAO header. Fall through.
        console.debug('[generateInvoicePdf] direct fetch blocked, trying proxy:', src);
      }

      try {
        img.src = await fetchAsDataUrl(proxied(src));
        await waitForImage(img);
      } catch (proxyErr) {
        // Only now is the image genuinely unusable. Hiding it keeps html2canvas
        // from tainting the canvas (which would fail the whole PDF), but this is
        // the branch that silently dropped every studio logo before the proxy
        // existed — so it is logged as a warning, not swallowed.
        console.warn('[generateInvoicePdf] Could not inline image, omitting from PDF:', src, proxyErr);
        img.style.display = 'none';
      }
    }),
  );

  return saved;
}

function restoreRemoteImages(saved: SavedImageState[]): void {
  for (const { img, src, display } of saved) {
    img.src = src;
    img.style.display = display;
  }
}

export async function generateInvoicePdf(elementId: string, filename: string): Promise<void> {
  const el = await waitForElementById(elementId);
  await waitForCaptureImages(el);

  const savedImages = await inlineRemoteImages(el);

  try {
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: false,
    });
    const img = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // Fit entire invoice on a single A4 page (scale down if needed, never add page 2)
    const ratio = canvas.width / canvas.height;
    let imgW = pageW;
    let imgH = pageW / ratio;
    if (imgH > pageH) {
      imgH = pageH;
      imgW = pageH * ratio;
    }
    const x = (pageW - imgW) / 2;
    pdf.addImage(img, 'PNG', x, 0, imgW, imgH);
    pdf.save(filename);
  } finally {
    restoreRemoteImages(savedImages);
  }
}
